import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Repeat, Calendar, Trash2, Edit, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import axios from 'axios';
import { API_URL } from '../config';

const API = API_URL;

const FREQUENCY_LABELS = {
  daily: 'Diaria',
  weekly: 'Semanal',
  biweekly: 'Quincenal',
  monthly: 'Mensual',
  yearly: 'Anual'
};

/**
 * Diálogo para gestionar acciones en tareas recurrentes
 * Permite elegir entre: solo esta instancia, esta y futuras, o toda la serie
 */
export const RecurringTaskDialog = ({
  isOpen,
  onClose,
  task,
  action, // "edit" | "delete"
  onConfirm,
  seriesInfo
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedMode, setSelectedMode] = useState('only_this');
  const [localSeriesInfo, setLocalSeriesInfo] = useState(seriesInfo);

  // Cargar info de la serie si no se proporciona
  useEffect(() => {
    const fetchSeriesInfo = async () => {
      if (isOpen && task && !seriesInfo) {
        try {
          const response = await axios.get(`${API}/tasks/${task.id}/series`);
          setLocalSeriesInfo(response.data);
        } catch (error) {
          console.error('Error fetching series info:', error);
          setLocalSeriesInfo({ is_series: false, total_count: 1 });
        }
      } else if (seriesInfo) {
        setLocalSeriesInfo(seriesInfo);
      }
    };
    
    fetchSeriesInfo();
  }, [isOpen, task, seriesInfo]);

  // Reset mode when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedMode('only_this');
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm(selectedMode);
      onClose();
    } catch (error) {
      console.error('Error in recurring task action:', error);
    } finally {
      setLoading(false);
    }
  };

  const info = localSeriesInfo || { is_series: false, total_count: 1 };
  const isRecurringSeries = info.is_series || task?.is_recurring || task?.parent_task_id;

  // Si no es una serie recurrente, ejecutar directamente
  if (!isRecurringSeries) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {action === 'delete' ? (
                <>
                  <Trash2 className="w-5 h-5 text-red-400" />
                  Eliminar Tarea
                </>
              ) : (
                <>
                  <Edit className="w-5 h-5 text-cyan-400" />
                  Editar Tarea
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {action === 'delete' 
                ? '¿Estás seguro de que deseas eliminar esta tarea?'
                : 'Se editará esta tarea individual.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
              <h4 className="font-semibold text-white">{task?.title}</h4>
              <p className="text-sm text-slate-400 mt-1">
                {task?.start_datetime && format(new Date(task.start_datetime), "EEEE d 'de' MMMM 'a las' HH:mm", { locale: es })}
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                onConfirm('only_this');
                onClose();
              }}
              className={action === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-cyan-600 hover:bg-cyan-700'}
            >
              {action === 'delete' ? 'Eliminar' : 'Continuar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {action === 'delete' ? (
              <>
                <Trash2 className="w-5 h-5 text-red-400" />
                Eliminar Tarea Recurrente
              </>
            ) : (
              <>
                <Edit className="w-5 h-5 text-cyan-400" />
                Editar Tarea Recurrente
              </>
            )}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Esta tarea es parte de una serie recurrente. Elige qué deseas {action === 'delete' ? 'eliminar' : 'editar'}.
          </DialogDescription>
        </DialogHeader>

        {/* Info de la tarea */}
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-semibold text-white">{task?.title}</h4>
              <p className="text-sm text-slate-400 mt-1">
                {task?.start_datetime && format(new Date(task.start_datetime), "EEEE d 'de' MMMM 'a las' HH:mm", { locale: es })}
              </p>
            </div>
            <Badge className="bg-purple-600/20 text-purple-300 border border-purple-500/30">
              <Repeat className="w-3 h-3 mr-1" />
              {FREQUENCY_LABELS[info.recurrence_frequency] || 'Recurrente'}
            </Badge>
          </div>
          
          {/* Estadísticas de la serie */}
          <div className="flex gap-4 mt-3 text-xs">
            <span className="text-slate-400">
              <Calendar className="w-3 h-3 inline mr-1" />
              {info.total_count} instancias en total
            </span>
            <span className="text-yellow-400">
              {info.pending_count} pendientes
            </span>
            <span className="text-green-400">
              {info.completed_count} completadas
            </span>
          </div>
        </div>

        {/* Opciones */}
        <div className="space-y-2 py-2">
          {/* Solo esta instancia */}
          <button
            onClick={() => setSelectedMode('only_this')}
            className={`w-full p-4 rounded-lg border text-left transition-all ${
              selectedMode === 'only_this'
                ? 'border-cyan-500 bg-cyan-900/20'
                : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                selectedMode === 'only_this' ? 'border-cyan-500' : 'border-slate-500'
              }`}>
                {selectedMode === 'only_this' && (
                  <div className="w-2 h-2 rounded-full bg-cyan-500" />
                )}
              </div>
              <div>
                <p className="font-medium text-white">Solo esta instancia</p>
                <p className="text-sm text-slate-400">
                  {action === 'delete' 
                    ? 'Elimina solo la tarea del ' + (task?.start_datetime ? format(new Date(task.start_datetime), "d 'de' MMMM", { locale: es }) : '')
                    : 'Edita solo esta fecha específica'
                  }
                </p>
              </div>
            </div>
          </button>

          {/* Esta y futuras */}
          <button
            onClick={() => setSelectedMode('this_and_future')}
            className={`w-full p-4 rounded-lg border text-left transition-all ${
              selectedMode === 'this_and_future'
                ? 'border-cyan-500 bg-cyan-900/20'
                : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                selectedMode === 'this_and_future' ? 'border-cyan-500' : 'border-slate-500'
              }`}>
                {selectedMode === 'this_and_future' && (
                  <div className="w-2 h-2 rounded-full bg-cyan-500" />
                )}
              </div>
              <div>
                <p className="font-medium text-white">Esta y todas las futuras</p>
                <p className="text-sm text-slate-400">
                  {action === 'delete' 
                    ? 'Elimina desde esta fecha en adelante (mantiene las pasadas)'
                    : 'Modifica esta y todas las instancias futuras'
                  }
                </p>
              </div>
            </div>
          </button>

          {/* Toda la serie */}
          <button
            onClick={() => setSelectedMode('all')}
            className={`w-full p-4 rounded-lg border text-left transition-all ${
              selectedMode === 'all'
                ? action === 'delete' ? 'border-red-500 bg-red-900/20' : 'border-cyan-500 bg-cyan-900/20'
                : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                selectedMode === 'all' 
                  ? action === 'delete' ? 'border-red-500' : 'border-cyan-500'
                  : 'border-slate-500'
              }`}>
                {selectedMode === 'all' && (
                  <div className={`w-2 h-2 rounded-full ${action === 'delete' ? 'bg-red-500' : 'bg-cyan-500'}`} />
                )}
              </div>
              <div>
                <p className="font-medium text-white flex items-center gap-2">
                  Toda la serie
                  {action === 'delete' && (
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                  )}
                </p>
                <p className="text-sm text-slate-400">
                  {action === 'delete' 
                    ? `Elimina las ${info.total_count} instancias (incluidas las completadas)`
                    : 'Modifica todas las instancias de la serie'
                  }
                </p>
              </div>
            </div>
          </button>
        </div>

        {/* Warning for delete all */}
        {action === 'delete' && selectedMode === 'all' && info.completed_count > 0 && (
          <div className="bg-amber-900/20 border border-amber-600/30 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-200">
              Se eliminarán {info.completed_count} tareas ya completadas. Esta acción no se puede deshacer.
            </p>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading}
            className={action === 'delete' 
              ? 'bg-red-600 hover:bg-red-700' 
              : 'bg-cyan-600 hover:bg-cyan-700'
            }
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Procesando...
              </span>
            ) : (
              action === 'delete' ? 'Eliminar' : 'Continuar'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecurringTaskDialog;
